'use client';

import ScrollFade from '@/components/ui/ScrollFade/ScrollFade';
import { createStagingInfo, odds, totalStock } from '@/features/lottery/utils';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { type MutableRefObject, useRef } from 'react';
import type { LotteryCustomOption, LotteryDrawHistoryLineup, LotteryPrize } from '../../types';
import LotteryGoodsList from '../LotteryGoodsList/LotteryGoodsList';
import LotteryPrizeStaging, { type LotteryPrizeStagingHandler } from '../LotteryPrizeStaging/LotteryPrizeStaging';
import styles from './LotteryPrizeList.module.scss';

function LotteryPrizes({
  lotteryStatus,
  prizes,
  lotteryDrawHistory,
  backgroundColor,
  isHideStaging = false,
  customOptions,
}: {
  lotteryStatus: string;
  prizes: Array<LotteryPrize>;
  lotteryDrawHistory?: Array<LotteryDrawHistoryLineup>;
  backgroundColor?: string;
  isHideStaging?: boolean;
  customOptions?: LotteryCustomOption;
}) {
  const stagingRef = useRef<LotteryPrizeStagingHandler>();
  const fadeinCallback = (playId: number) => {
    // stagingRankのアニメーション発火
    stagingRef.current?.play(playId);
  };

  const isShowOdds = (prize: LotteryPrize) => {
    if (customOptions?.hideOdds === '1') {
      return false;
    }
    return !!prize.odds;
  };

  const showRankAnimation = () => {
    if (isHideStaging) return false;
    return customOptions?.hidePrizeLabel !== '1';
  };

  const generateStyle = (prize: LotteryPrize) => {
    if (!prize.stagingKey.includes('A')) return {};
    return {
      background: backgroundColor,
    };
  };

  return (
    <>
      <ul className={styles.componentStyle}>
        {prizes.map((prize) => (
          <ScrollFade tag={'li'} key={`prize_${prize.id}`} fadeinCallback={() => fadeinCallback(prize.id)}>
            <div className={styles.itemCard} style={generateStyle(prize)}>
              {/* 在庫・確率 */}
              <div className={styles.statusWrapper}>
                {totalStock(prize.goods) === 0 && <p className={styles.outstock}>品切れ</p>}
                {isShowOdds(prize) && (
                  <p>
                    当選確率:&nbsp;
                    {odds(prize.id, prize.odds, lotteryDrawHistory)}%
                  </p>
                )}
              </div>
              {/* ランクアニメーション */}
              {showRankAnimation() && lotteryPrizeStaging(prize.id, prize.stagingKey, stagingRef)}

              {/* 景品ラベル名 */}
              <div className={`${styles.itemName} ${utilsStyles.textLarge} ${utilsStyles.textBold}`}>{prize.name}</div>
              {/* 景品ラベル詳細 */}
              <div className={`${styles.itemDescription} ${utilsStyles.textSmall}`}>{prize.description}</div>
              {/* 景品一覧 */}
              <LotteryGoodsList
                goodsList={prize.goods}
                linupId={prize.id}
                lotteryDrawHistory={lotteryDrawHistory}
                liKeyPrefix={'prize_goods'}
                lotteryStatus={lotteryStatus}
              />
            </div>
          </ScrollFade>
        ))}
      </ul>
    </>
  );
}

function lotteryPrizeStaging(
  prizeId: number,
  stagingKey: string,
  ref: MutableRefObject<LotteryPrizeStagingHandler | undefined>,
) {
  const stagingInfo = createStagingInfo(stagingKey);
  return (
    <div className={styles.itemStaging}>
      <LotteryPrizeStaging
        id={prizeId}
        stagingRank={stagingInfo.stagingRank}
        stagingText={stagingInfo.stagingText}
        customRef={ref}
      />
    </div>
  );
}

export default LotteryPrizes;
